import { useEffect, useState, type SyntheticEvent } from "react";
import { Link } from "react-router";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Button, Card, Input } from "@contractor-quoting/shared-ui";

export function ContractorDashboard() {
  interface DashboardData {
    metrics: {
      new_leads: number;
      pending_quotes: number;
      accepted_jobs: number;
      walkthroughs: number;
    };
    leads: Array<{
      id: string;
      title: string | null;
      project_type: string;
      customer_name: string | null;
      customer_email: string | null;
      status: string;
    }>;
    schedule: Array<{ id: string; title: string; starts_at: string | null }>;
    activity: Array<{ id: string; summary: string; created_at: string }>;
  }
  const [data, setData] = useState<DashboardData | null>(null);
  const [customerEmail, setCustomerEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteError, setInviteError] = useState(false);
  const [inviting, setInviting] = useState(false);
  useEffect(() => {
    void apiFetch<DashboardData>("/v1/contractor/dashboard").then(setData);
  }, []);
  if (!data) return <p>Loading dashboard…</p>;
  const inviteCustomer = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setInviting(true);
    setInviteMessage("");
    setInviteError(false);
    try {
      await apiFetch("/v1/contractor/customer-project-invitations", {
        method: "POST",
        body: JSON.stringify({ email: customerEmail }),
      });
      setCustomerEmail("");
      setInviteMessage("Invitation created. Email delivery is pending.");
    } catch (reason) {
      setInviteError(true);
      setInviteMessage(reason instanceof Error ? reason.message : "Invitation could not be sent.");
    } finally {
      setInviting(false);
    }
  };
  const metrics = [
    ["New leads", data.metrics?.new_leads ?? 0],
    ["Pending quotes", data.metrics?.pending_quotes ?? 0],
    ["Accepted jobs", data.metrics?.accepted_jobs ?? 0],
    ["Walkthroughs", data.metrics?.walkthroughs ?? 0],
  ];
  return (
    <div>
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
        Contractor dashboard
      </p>
      <h1 className="mt-2 text-4xl font-black text-emerald-950">Dashboard</h1>
      <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map(([label, value]) => (
          <Card key={String(label)}>
            <p className="text-3xl font-black">{String(value)}</p>
            <p className="mt-1 text-sm text-stone-500">{label}</p>
          </Card>
        ))}
      </div>
      <Card className="mt-6">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)] lg:items-end">
          <div>
            <h2 className="text-xl font-black">Invite a customer to submit a project</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
              We’ll email the customer from the Mezoniq no-reply address. Their project link opens
              the chat with your business already selected.
            </p>
          </div>
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(event) => void inviteCustomer(event)}
          >
            <label className="min-w-0 flex-1">
              <span className="sr-only">Customer email</span>
              <Input
                aria-label="Customer email"
                autoComplete="email"
                onChange={(event) => setCustomerEmail(event.target.value)}
                placeholder="customer@example.com"
                required
                type="email"
                value={customerEmail}
              />
            </label>
            <Button disabled={inviting || !customerEmail.trim()} type="submit">
              {inviting ? "Creating invitation…" : "Send project invitation"}
            </Button>
          </form>
        </div>
        {inviteMessage ? (
          <p
            aria-live="polite"
            className={`mt-3 text-sm font-semibold ${inviteError ? "text-red-700" : "text-emerald-700"}`}
          >
            {inviteMessage}
          </p>
        ) : null}
      </Card>
      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black">New leads</h2>
            <Link className="text-sm font-bold text-emerald-800" to="/contractor/leads">
              View all
            </Link>
          </div>
          <div className="mt-4 divide-y">
            {data.leads.map((lead) => (
              <Link
                className="flex items-center justify-between py-3"
                key={lead.id}
                to={`/contractor/leads/${lead.id}`}
              >
                <div>
                  <p className="font-bold">{lead.title || lead.project_type}</p>
                  <p className="text-sm text-stone-500">
                    {lead.customer_name || lead.customer_email}
                  </p>
                </div>
                <Badge>{lead.status.replaceAll("_", " ")}</Badge>
              </Link>
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="text-xl font-black">Upcoming walkthroughs</h2>
          <div className="mt-4 divide-y">
            {data.schedule.map((item) => (
              <div className="py-3" key={item.id}>
                <p className="font-bold">{item.title}</p>
                <p className="text-sm text-stone-500">
                  {item.starts_at
                    ? new Date(item.starts_at).toLocaleString()
                    : "Awaiting a confirmed time"}
                </p>
              </div>
            ))}
          </div>
        </Card>
        <Card className="xl:col-span-2">
          <h2 className="text-xl font-black">Recent activity</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.activity.map((item) => (
              <div className="rounded-xl bg-stone-50 p-3" key={item.id}>
                <p className="font-semibold">{item.summary}</p>
                <p className="mt-1 text-xs text-stone-500">
                  {new Date(item.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const endpointTitles: Record<string, string> = {
  leads: "Leads",
  quotes: "Final quotes",
  schedule: "Walkthrough schedule",
  files: "Private files",
};
export function ContractorEndpointPage({
  endpoint,
}: {
  endpoint: "leads" | "quotes" | "schedule" | "files";
}) {
  interface EndpointItem {
    id?: string;
    project_id?: string;
    title?: string;
    quote_number?: string;
    filename?: string;
    customer_name?: string;
    customer_email?: string;
    starts_at?: string;
    content_type?: string;
    status?: string;
    total_minor?: string;
    currency?: string;
    readUrl?: string;
  }
  const [items, setItems] = useState<EndpointItem[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    void apiFetch<{ items: EndpointItem[] }>(`/v1/contractor/${endpoint}`)
      .then((result) => setItems(result.items))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Unavailable"),
      );
  }, [endpoint]);
  return (
    <div>
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
        Contractor dashboard
      </p>
      <h1 className="mt-2 text-4xl font-black text-emerald-950">{endpointTitles[endpoint]}</h1>
      {error ? <Card className="mt-6 text-red-700">{error}</Card> : null}
      <div className="mt-7 grid gap-4">
        {items.map((item, index) => (
          <Card
            key={item.id ?? index}
            className={
              endpoint === "quotes"
                ? "grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_auto_140px]"
                : "flex flex-wrap items-center justify-between gap-3"
            }
          >
            <div>
              <h2 className="font-bold">
                {item.title ||
                  item.quote_number ||
                  item.filename ||
                  item.customer_name ||
                  item.customer_email ||
                  `Record ${index + 1}`}
              </h2>
              <p className="mt-1 text-sm text-stone-500">
                {item.customer_email ||
                  (item.starts_at && new Date(item.starts_at).toLocaleString()) ||
                  item.content_type ||
                  ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {item.status ? <Badge>{item.status.replaceAll("_", " ")}</Badge> : null}
              {item.project_id || (item.id && endpoint === "leads") ? (
                <Link
                  className="rounded-lg border px-3 py-2 text-sm font-bold"
                  to={
                    endpoint === "leads"
                      ? `/contractor/leads/${item.id}`
                      : `/contractor/projects/${item.project_id}`
                  }
                >
                  Open
                </Link>
              ) : null}
              {item.readUrl ? (
                <a
                  className="rounded-lg border px-3 py-2 text-sm font-bold"
                  href={item.readUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View
                </a>
              ) : null}
            </div>
            {endpoint === "quotes" ? (
              <p className="text-left text-lg font-black sm:text-right">
                {item.total_minor
                  ? new Intl.NumberFormat("en-CA", {
                      style: "currency",
                      currency: item.currency ?? "CAD",
                    }).format(Number(item.total_minor) / 100)
                  : "—"}
              </p>
            ) : null}
          </Card>
        ))}
        {items.length === 0 ? <Card>No records yet.</Card> : null}
      </div>
    </div>
  );
}
